import { ISODateString } from '@mesa-labs/mesa-api/dist/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type DashboadTableTab = 'invoices' | 'payments' | 'openInvoices' | 'paidInvoices' | 'collections' | 'adjustments';
export type PaidInvoicesTableTab = 'Invoices' | 'Payments';

interface UIState {
  activeDashboardTableTab: DashboadTableTab;
  activePaidInvoicesTableTab: PaidInvoicesTableTab;
  discoverMoreOpen: boolean;
  excludeSupplementalFee: boolean;
  hasSeenGetPaidNow: boolean;
  inputMfaCodeComplete: boolean;
  inputSmsCodeComplete: boolean;
  payEarlyButtonClickCount: number;
  showEarlyPaymentOpportunitiesGlobalNotification: boolean;
  singleToBulkInterstitialClickTimestamps: ISODateString[];
  businessInfoLastUpdatedAt: ISODateString;
  factoringBannerDismissed: boolean;
  factoringReviewBannerDismissed: boolean;
  factoringPortalSetupBannerDismissed: boolean;
}

const initialState: UIState = {
  activeDashboardTableTab: 'invoices',
  activePaidInvoicesTableTab: 'Invoices',
  discoverMoreOpen: true,
  excludeSupplementalFee: false,
  hasSeenGetPaidNow: false,
  inputMfaCodeComplete: false,
  inputSmsCodeComplete: false,
  payEarlyButtonClickCount: 0,
  showEarlyPaymentOpportunitiesGlobalNotification: false,
  singleToBulkInterstitialClickTimestamps: [],
  businessInfoLastUpdatedAt: '',
  factoringBannerDismissed: false,
  factoringReviewBannerDismissed: false,
  factoringPortalSetupBannerDismissed: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    updateInputMfaCodeComplete(state: UIState, action: PayloadAction<boolean>) {
      state.inputMfaCodeComplete = action.payload;
    },
    updateInputSmsCodeComplete(state: UIState, action: PayloadAction<boolean>) {
      state.inputSmsCodeComplete = action.payload;
    },
    updateHasSeenGetPaidNow(state: UIState, action: PayloadAction<boolean>) {
      state.hasSeenGetPaidNow = action.payload;
    },
    updateExcludeSupplementalFee(state: UIState, action: PayloadAction<boolean>) {
      state.excludeSupplementalFee = action.payload;
    },
    updateSingleToBulkInterstitialClickTimestamps(state: UIState, action: PayloadAction<ISODateString>) {
      // clear this out after 20 entries to avoid too large of a record:
      if (state.singleToBulkInterstitialClickTimestamps.length === 19) {
        state.singleToBulkInterstitialClickTimestamps = [];
      } else {
        state.singleToBulkInterstitialClickTimestamps.push(action.payload);
      }
    },
    updateShowEarlyPaymentOpportunitiesGlobalNotification(state: UIState, action: PayloadAction<boolean>) {
      state.showEarlyPaymentOpportunitiesGlobalNotification = action.payload;
    },
    updateDiscoverMoreOpen(state: UIState, action: PayloadAction<boolean>) {
      state.discoverMoreOpen = action.payload;
    },
    updatePayEarlyButtonClickCount(state: UIState, action: PayloadAction<number>) {
      state.payEarlyButtonClickCount = action.payload;
    },
    updateActiveDashboardTableTab(state: UIState, action: PayloadAction<DashboadTableTab>) {
      state.activeDashboardTableTab = action.payload;
    },
    updateActivePaidInvoicesTableTab(state: UIState, action: PayloadAction<PaidInvoicesTableTab>) {
      state.activePaidInvoicesTableTab = action.payload;
    },
    updateBusinessInfoLastUpdatedAt(state: UIState, action: PayloadAction<ISODateString>) {
      state.businessInfoLastUpdatedAt = action.payload;
    },
    updateFactoringBannerDismissed(state: UIState, action: PayloadAction<boolean>) {
      state.factoringBannerDismissed = action.payload;
    },
    updateFactoringReviewBannerDismissed(state: UIState, action: PayloadAction<boolean>) {
      state.factoringReviewBannerDismissed = action.payload;
    },
    updateFactoringPortalSetupBannerDismissed(state: UIState, action: PayloadAction<boolean>) {
      state.factoringPortalSetupBannerDismissed = action.payload;
    },
  },
});

export const {
  updateActiveDashboardTableTab,
  updateActivePaidInvoicesTableTab,
  updateDiscoverMoreOpen,
  updateExcludeSupplementalFee,
  updateHasSeenGetPaidNow,
  updateInputMfaCodeComplete,
  updateInputSmsCodeComplete,
  updatePayEarlyButtonClickCount,
  updateShowEarlyPaymentOpportunitiesGlobalNotification,
  updateSingleToBulkInterstitialClickTimestamps,
  updateBusinessInfoLastUpdatedAt,
  updateFactoringBannerDismissed,
  updateFactoringReviewBannerDismissed,
  updateFactoringPortalSetupBannerDismissed,
} = uiSlice.actions;
export default uiSlice.reducer;
